import { useLayoutEffect, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { Box } from '@mui/material'
import { PageTitle } from '../components/atoms/PageTitle'
import { callAxios } from '../hooks/useAxios'
import { CounterData, CounterName, TelemetryData, isPortStatistics } from '../types/api'
import { PortNumber } from '../types/ports'
import { sprintf } from 'sprintf-js'
import { PageSubtitle } from '../components/atoms/PageSubtitle'
import { CheckBox, Error } from '@mui/icons-material'

export function AdminTelemetryPage(): React.ReactElement {

  const { t } = useTranslation()
  const [data, setData] = useState<TelemetryData | null>(null)

  useLayoutEffect(() => {
    const updateData = () => {
      callAxios<TelemetryData | null>({
        url: '/telemetry',
      }).then(res => res.success && setData(res.data))
    }

    updateData()
    const interval = setInterval(updateData, 10000)
    return () => {
      clearInterval(interval)
    }
  }, [])

  const started = data && data.started
  const ports = data
    ? Object.keys(data).filter(k => k !== 'started' && k !== 'counters' && k !== 'latest')
      .sort((a, b) => data[b as PortNumber].count * data[b as PortNumber].avg - data[a as PortNumber].count * data[a as PortNumber].avg)
    : []
  const counters: CounterData = (data && data.counters) || {}
  const uptime = started ? Math.round((new Date().getTime() - new Date(started).getTime()) / 1000) : 0

  let totalCount = 0
  let totalTime = 0

  data && Object.values(data).forEach(stats => {
    if (isPortStatistics(stats)) {
      totalCount += stats.count
      totalTime += stats.count * stats.avg
    }
  })

  let sum = 0
  Object.keys(counters).forEach(k => {
    if (k !== 'started' && k !== 'total' && !k.endsWith('_gw')) {
      sum += counters[k as CounterName] || 0
    }
  })

  const ok = sum === counters.total

  return (
    <Box>
      <PageTitle title={t('Telemetry')}/>
      { data && started && <Box>
          <Trans>Uptime</Trans>: {sprintf('%.1f', uptime / 60)} min
        </Box>
      }
      <PageSubtitle title={t('Totals')}/>
      <Box sx={{ mt: 2, justifyContent: 'center' }}>
        {
          Object.keys(counters).map(key => {
            return key !== 'started' &&
            <Box key={key} sx={{ ml: '25%', color: key === 'total' ? 'primary.main' : '' }}>
              {key}: {counters[key as CounterName]}
              {key === 'total' && (ok ? <> <CheckBox sx={{ mb: -1 }}/></> : <> <Error sx={{ mb: -1 }}/>(&minus;{(counters.total || 0) - sum})</>)}
            </Box>
          })
        }
      </Box>
      <PageSubtitle title={t('Processing Time')}/>
      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
        {!data && <Trans>No Data</Trans>}
        {data && started &&
          <Box>
            <Box sx={{ mt: 1 }}>
            {
              ports.map((port) =>
                <Box key={port}>
                  <Box sx={{ color: 'primary.main' }}>{port} x {data[port as PortNumber].count},</Box>
                  <Box sx={{ ml: 2 }}>latest processing {sprintf('%.1f', data.latest[port as PortNumber])} ms,</Box>
                  <Box sx={{ ml: 2 }}>average processing {sprintf('%.1f', data[port as PortNumber].avg)} ms,</Box>
                  <Box sx={{ ml: 2 }}>range {sprintf('%.1f', data[port as PortNumber].min)} ms .. {sprintf('%.1f', data[port as PortNumber].max)} ms,</Box>
                  <Box sx={{ ml: 2 }}>rate {sprintf('%.1f', data[port as PortNumber].count / (uptime / 60))} / min</Box>
              </Box>)
            }
            </Box>
            <Box sx={{ mt: 1 }}>
              <Box sx={{ color: 'secondary.main' }}><Trans>Total</Trans>: {totalCount}</Box>
              <Box sx={{ ml: 2 }}>{sprintf('%.0f', totalCount / (uptime / 60))} <Trans>events</Trans> / min</Box>
              <Box sx={{ ml: 2 }}>{sprintf('%.1f', totalTime / 1000)} sec ({sprintf('%.3f', (totalTime / 1000) / uptime)} %) <Trans>of time spend processing</Trans></Box>
            </Box>
          </Box>
        }
      </Box>
    </Box>
  )
}
